import type { HowItWorksContent } from "../types";

export const howItWorks: HowItWorksContent = {
  meta: {
    title: "How It Works | Flex Fund Prop Firm Evaluation",
    description:
      "Four steps from fee to funded — choose your account, pass our 1-step or 2-step evaluation, trade live capital, and withdraw weekly profits.",
  },
  hero: {
    line1: "How you get",
    line2Highlight: "funded.",
    lede: "Four steps. No surprises. Every rule the same on day 1 as on day 1,000.",
  },
  steps: {
    stepLabel: "STEP",
    cards: {
      chooseAndPay: {
        title: "Choose & pay",
        desc: "Pick a model and size. One-time fee. No subscription, no hidden charges.",
        metaLeft: "5 minutes",
        metaRight: "from $79",
      },
      passEvaluation: {
        title: "Pass evaluation",
        desc: "Hit the profit target while staying within the drawdown and risk-management limits.",
        metaLeft: "No time limit",
        metaRight: "1-Step or 2-Step",
      },
      tradeLive: {
        title: "Trade live capital",
        desc: "MetaTrader 5 with STP execution and tier-1 liquidity. Funded-account-specific rules apply.",
        metaLeft: "Live MT5",
        metaRight: "STP · tier-1",
      },
      withdrawShare: {
        title: "Withdraw your share",
        desc: "Regular payout windows. Bank transfer or USDC / USDT, processed in 48–72 business hours.",
        metaLeft: "Regular windows",
        metaRight: "80–90% split",
      },
    },
  },
  rules: {
    title: { line1: "1-Step or 2-Step.", line2Highlight: "Same payouts." },
    headers: {
      rule: "RULE",
      oneStep: "1-STEP",
      oneStepSub: "Faster path",
      twoStep: "2-STEP",
      twoStepSub: "Better terms",
    },
    labels: {
      profitTarget: "Profit target",
      dailyLoss: "Daily loss limit",
      maxDrawdown: "Max drawdown",
      minTradingDays: "Min trading days",
      profitSplit: "Profit split",
      timeLimit: "Time limit",
      weekendHolding: "Weekend holding",
    },
    words: { none: "None", allowed: "Allowed", upTo: "Up to" },
  },
  example: {
    title: { lead: "Anatomy of a ", highlight: "2-Step lifecycle", tail: "." },
    sub: "A $100K · 2-Step trader, broken down at every checkpoint. Every figure matches what your dashboard would display.",
    steps: {
      payFee: {
        phase: "BUY",
        title: "Pay the fee",
        body: "Pick the $100K · 2-Step evaluation. One-time fee, no subscription, no recurring charges.",
        keyLabel: "ONE-TIME FEE",
      },
      phase1: {
        phase: "PHASE 1",
        title: "Hit 8% profit",
        body: "Reach $108,000 from a $100,000 starting balance. No time pressure.",
        keyLabel: "PHASE 1 TARGET",
      },
      phase2: {
        phase: "PHASE 2",
        title: "Hit 5% profit",
        body: "Fresh $100,000 balance. Reach $105,000 to confirm consistency and unlock funded status.",
        keyLabel: "PHASE 2 TARGET",
      },
      withdraw: {
        phase: "FUNDED",
        title: "Withdraw 80%",
        body: "Generate $5,000 across a payout cycle on the funded account.",
        keyLabel: "YOUR SHARE",
      },
    },
    footnote:
      "Numbers based on the $100K · 2-Step program at the standard 80% split. Different account sizes scale linearly. The percentages above apply to whichever starting balance you pick.",
  },
  scaling: {
    title: { lead: "Eligible every 4 months. ", highlight: "Up to $1M." },
    sub: "Consistent traders unlock larger allocations on a documented schedule. No negotiations, no arbitrary milestones.",
    milestonePath: "Milestone path",
    ladder: {
      funded: { cycle: "FUNDED", size: "Your starting tier", delta: "$10K – $200K" },
      mo4: { cycle: "+4 MO", size: "First scale-up", delta: "Eligible" },
      mo8: { cycle: "+8 MO", size: "Second scale-up", delta: "Eligible" },
      mo12: { cycle: "+12 MO", size: "Third scale-up", delta: "Eligible" },
      mo20: { cycle: "+20 MO", size: "Up to the flagship cap", delta: "$1M" },
    },
    eligibilityTitle: "Eligibility · every 4 months",
    eligibilitySub: "Hit four checkpoints to unlock the next allocation.",
    criteria: [
      "+10% net profit over the cycle",
      "2 successful payouts completed",
      "Positive balance maintained",
      "No rule violations during the period",
    ],
    note: "Risk limits scale proportionally with your new balance. The same percentage rules apply, only the absolute numbers grow.",
  },
  faq: {
    title: { lead: "Common ", highlight: "questions", tail: "." },
    sub: "Browse the full library on the Flex Fund knowledge base. Deeper answers, richer detail.",
    browseAll: "Browse all FAQs",
    items: {
      timeLimit: {
        q: "Is there a time limit on the evaluation?",
        a: "No. Professional traders trade when the market presents an edge, not when a stopwatch dictates. Take as long as you need to reach the target.",
        category: "RULES",
      },
      payoutFrequency: {
        q: "How often are payouts processed?",
        a: "Weekly. Your first withdrawal is available on day 7 of trading your funded account, and you can withdraw every 7 days after that. Funds are sent via bank transfer, USDC/USDT or card.",
        category: "PAYOUTS",
      },
      profitSplit: {
        q: "What profit split do I get?",
        a: "Every account starts at an 80% split, regardless of size. You can upgrade to a 90% split via the optional add-on at checkout. One lever, no scaling tiers, no surprises. The split you pick is locked in for the life of your account.",
        category: "PAYOUTS",
      },
      oneVsTwoStep: {
        q: "What's the difference between 1-Step and 2-Step?",
        a: "2-Step is a two-phase evaluation (8% then 5% target) with conservative drawdown limits. 1-Step compresses the evaluation into a single 10% target with tighter risk rules. Higher fee, faster path to funded.",
        category: "RULES",
      },
      easCopyTrading: {
        q: "Can I use EAs and copy-trading?",
        a: "Yes, expert advisors and copy-trading are permitted on both evaluation models. Standard restrictions apply on news events and HFT-style abuse. Full details in the trader agreement.",
        category: "PLATFORM",
      },
      ruleBreach: {
        q: "What happens if I breach a rule?",
        a: "Account closure is final on a max-drawdown breach, but your trader profile and history carry over to your next account. We don't offer free resets. Pricing is one-time and stays transparent.",
        category: "RULES",
      },
    },
  },
};
